import { useEffect, useState } from 'react';
import { CalendarDays, Clock } from 'lucide-react';

/**
 * The Dashboard's content for App.tsx's topbar left slot (published through
 * lib/topbarFocus's useTopbarContent): greeting, welcome line, then the date
 * and a clock ticking each second — docs/design/dashboard regions 2-3. Moving
 * these out of the page frees a row of dashboard height.
 *
 * Everything is formatted in the hospital timezone (queue_settings.timezone,
 * returned by the appointment summary), not the browser's — a desk PC with a
 * wrong system zone must not greet the morning shift with "Good evening".
 */
export function DashboardGreeting({ name, timezone }: { name: string; timezone: string | null }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const tz = safeZone(timezone);
  const hour = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone: tz }).format(now));
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: tz })
      .formatToParts(now).map(p => [p.type, p.value])
  );
  const date = `${parts.weekday}, ${parts.day} ${parts.month} ${parts.year}`;
  const time = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true, timeZone: tz }).format(now);

  return (
    <div className="flex min-w-0 items-center gap-5 md:pl-4">
      <div className="min-w-0">
        <div className="truncate text-[15px] font-bold leading-tight text-ink md:text-[18px]">{greeting}, {name} <span aria-hidden>👋</span></div>
        <div className="hidden truncate text-[12.5px] leading-tight text-ink-muted md:block">Welcome back! Here&apos;s what&apos;s happening today.</div>
      </div>
      <div className="hidden h-9 w-px shrink-0 bg-line xl:block" />
      <div className="hidden shrink-0 text-[12.5px] leading-tight text-ink xl:block">
        <div className="flex items-center gap-2 whitespace-nowrap"><CalendarDays className="h-3.5 w-3.5 text-teal" />{date}</div>
        <div className="mt-0.5 flex items-center gap-2 whitespace-nowrap tabular-nums"><Clock className="h-3.5 w-3.5 text-teal" />{time}</div>
      </div>
    </div>
  );
}

function safeZone(tz: string | null): string | undefined {
  if (!tz) return undefined;
  try { new Intl.DateTimeFormat('en-GB', { timeZone: tz }); return tz; } catch { return undefined; }
}
